import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import type { Job } from "../../../store/jobStore";
import type { JobStatusSnapshot } from "../api/jobStatusApi";

/** Server statuses the UI folds into PENDING — the drawer/badge/max-jobs
 *  logic only ever sees PENDING for a running pipeline. */
const RESUMABLE_STATUSES = new Set<string>([
  ReportJobStatus.PENDING,
  ReportJobStatus.STARTED,
  ReportJobStatus.GENERATING,
  ReportJobStatus.EXTRACTING,
]);

/** Legacy failure copy, preserved verbatim from the old checkJobStatus. */
export function jobFailureMessage(error: unknown): string {
  let message =
    "An error occurred while processing the video. Error found: " +
    ((error as Error)?.message || "");
  if (isAxiosError(error) && error.response) {
    message += " " + (error.response.data?.error || "");
  }
  return message;
}

/** The failure copy for a server-reported FAILED status (the old code threw
 *  `new Error(message || "Video processing failed.")` into the same path). */
export function jobFailedStatusMessage(snapshotMessage?: string): string {
  return jobFailureMessage(
    new Error(snapshotMessage || "Video processing failed."),
  );
}

/**
 * Pure transition reducer: folds a /jobStatus snapshot into a registry Job,
 * reproducing the old updateJobStatus/updateJobProgress/updateJobCompletionData
 * composition. Returns the SAME reference when nothing changed, so the
 * query-sync effect can re-apply snapshots idempotently without re-renders.
 *
 * Dropped (deliberately): the old `reportName: reportId` clobber inside
 * updateJobStatus — nothing ever set reportName, so it was a no-op.
 */
export function applyJobStatusSnapshot(job: Job, snapshot: JobStatusSnapshot): Job {
  const { jobStatus, message } = snapshot;

  let next: Job;
  if (jobStatus === ReportJobStatus.AWAITING_INPUT) {
    next = {
      ...job,
      status: ReportJobStatus.AWAITING_INPUT,
      error: undefined,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  } else if (jobStatus === ReportJobStatus.PENDING_REVIEW) {
    next = {
      ...job,
      status: ReportJobStatus.PENDING_REVIEW,
      reportId: snapshot.reportId ?? job.reportId,
      progress: snapshot.progress ?? 100,
      progressMessage: message || job.progressMessage,
    };
  } else if (jobStatus === ReportJobStatus.FAILED) {
    next = {
      ...job,
      status: ReportJobStatus.FAILED,
      error: jobFailedStatusMessage(message),
    };
  } else if (RESUMABLE_STATUSES.has(jobStatus)) {
    next = {
      ...job,
      status: ReportJobStatus.PENDING,
      error: undefined,
      // Resuming after human input starts a fresh visible activity cycle.
      displayStartedAt:
        job.status === ReportJobStatus.AWAITING_INPUT
          ? new Date()
          : job.displayStartedAt,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  } else {
    // Unknown status: merge progress only, keep polling semantics upstream.
    next = {
      ...job,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  }

  const changed = (Object.keys(next) as (keyof Job)[]).some(
    (key) => next[key] !== job[key],
  );
  return changed ? next : job;
}
